import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FaRobot, FaPaperPlane, FaMicrophone, FaStopCircle, FaRegCopy, FaCheck,
  FaRedo, FaArrowRight, FaArrowLeft, FaBolt, FaCommentDots, FaMagic, FaUser,
} from 'react-icons/fa';
import { sendChat } from '../../services/chatService';
import { useAuth } from '../../contexts/AuthContext';
import { ACTIONS, ACTION_GROUPS, QUICK_ASKS } from './actions';

/* ============================================================
   Copiloto del candidato — dos modos:
   · Consultar: chat con acceso en vivo a toda la plataforma.
   · Gestionar: acciones de IA que devuelven borradores listos.
   Reusa el backend del asistente (/api/chat → askAssistant).
   ============================================================ */

const mono = 'var(--font-mono)';
const accent = 'var(--btn-primary)';

/* ---------- animaciones ---------- */
const dotBounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.35; }
  30%           { transform: translateY(-4px); opacity: 1; }
`;
const pulseMic = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(239, 71, 111, 0.5); }
  50%      { box-shadow: 0 0 0 6px rgba(239, 71, 111, 0); }
`;

/* ---------- layout ---------- */
const Page = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const Head = styled.header`
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
  margin-bottom: 1rem;

  .badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    flex-shrink: 0;
    border-radius: var(--radius);
    font-size: 1.2rem;
    color: ${accent};
    background: rgba(0, 180, 216, 0.12);
    border: 1px solid rgba(0, 180, 216, 0.32);
  }
  h1 {
    margin: 0;
    font-size: 1.5rem;
    line-height: 1.1;
    color: var(--text-primary);
  }
  p {
    margin: 0.3rem 0 0;
    font-size: 0.9rem;
    color: var(--text-secondary);
  }
`;

const Tabs = styled.div`
  display: inline-flex;
  gap: 0.3rem;
  padding: 0.25rem;
  background: var(--surface-deep);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius-pill);
  margin-bottom: 1.25rem;
`;

const TabBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: var(--radius-pill);
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.16s ease, color 0.16s ease;
  color: ${({ $active }) => ($active ? 'var(--text-primary)' : 'var(--text-disabled)')};
  background: ${({ $active }) => ($active ? 'rgba(0, 180, 216, 0.18)' : 'transparent')};

  svg { font-size: 0.85rem; }
  &:hover { color: var(--text-primary); }
`;

/* ---------- chat (consultar) ---------- */
const Thread = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
  min-height: 120px;
`;

const MsgRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  align-items: ${({ $user }) => ($user ? 'flex-end' : 'flex-start')};
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-family: ${mono};
  font-size: 0.6rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--text-disabled);
  svg { font-size: 0.7rem; color: ${accent}; }
`;

const Bubble = styled.div`
  max-width: 88%;
  padding: 0.8rem 1rem;
  border-radius: 10px;
  font-size: 0.92rem;
  line-height: 1.55;
  border: 1px solid var(--border-soft);

  ${({ $user, $error }) => {
    if ($error) return css`background: rgba(239,71,111,0.08); border-color: rgba(239,71,111,0.35); color: #ffc9d4;`;
    if ($user) return css`background: rgba(0,180,216,0.12); border-color: rgba(0,180,216,0.3); color: #eafaff;`;
    return css`background: var(--color-tertiary); border-left: 2px solid ${accent}; border-radius: 4px 10px 10px 10px;`;
  }}
`;

const TypingBubble = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  background: var(--color-tertiary);
  border: 1px solid var(--border-soft);
  border-left: 2px solid ${accent};
  padding: 0.55rem 0.9rem;
  border-radius: 4px 10px 10px 10px;
  color: var(--text-disabled);
  font-family: ${mono};
  font-size: 0.68rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;
const Dots = styled.span`
  display: inline-flex;
  gap: 4px;
  span { width: 6px; height: 6px; background: ${accent}; border-radius: 50%; animation: ${dotBounce} 1.2s infinite ease-in-out; }
  span:nth-child(2) { animation-delay: 0.15s; }
  span:nth-child(3) { animation-delay: 0.3s; }
`;

/* markdown */
const Md = styled.div`
  p { margin: 0 0 0.6em; }
  p:last-child { margin-bottom: 0; }
  h1, h2, h3, h4 { margin: 0.7em 0 0.4em; color: var(--text-primary); font-weight: 600; line-height: 1.3; }
  h1 { font-size: 1.12em; } h2 { font-size: 1.06em; } h3 { font-size: 1em; }
  ul, ol { margin: 0 0 0.6em 1.2em; } li { margin-bottom: 0.2em; }
  a { color: ${accent}; text-decoration: underline; }
  blockquote { border-left: 2px solid ${accent}; padding-left: 0.8em; margin: 0.5em 0; color: var(--text-secondary); }
  code { font-family: ${mono}; font-size: 0.85em; padding: 1px 5px; border-radius: 3px; background: rgba(0,0,0,0.35); color: #cfe9f4; }
  pre { font-family: ${mono}; background: rgba(0,0,0,0.4); border: 1px solid var(--border-soft); padding: 0.75em; border-radius: 6px; overflow-x: auto; }
  table { border-collapse: collapse; width: 100%; margin: 0.5em 0; font-size: 0.88em; }
  th, td { border: 1px solid var(--border-soft); padding: 4px 8px; text-align: left; }
  th { background: rgba(255,255,255,0.04); }
  strong { color: var(--text-primary); }
`;

/* input */
const Composer = styled.form`
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  padding: 0.35rem 0.35rem 0.35rem 0.9rem;
  background: rgba(11, 19, 32, 0.85);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  backdrop-filter: blur(8px);

  &:focus-within { border-color: rgba(0, 180, 216, 0.5); box-shadow: 0 0 0 3px rgba(0, 180, 216, 0.1); }

  textarea {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    color: var(--text-primary);
    font-family: inherit;
    font-size: 0.95rem;
    resize: none;
    padding: 0.55rem 0.25rem;
    min-height: 42px;
    max-height: 160px;
    line-height: 1.45;
    &::placeholder { color: var(--text-disabled); }
  }
`;

const RoundBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.12s ease, background 0.16s ease, border-color 0.16s ease;
  border: 1px solid ${({ $variant }) => ($variant === 'send' ? 'rgba(0,180,216,0.45)' : 'var(--border-soft)')};
  background: ${({ $variant, $on }) =>
    $variant === 'send' ? 'linear-gradient(135deg, rgba(0,180,216,0.3), rgba(0,180,216,0.15))'
    : $on ? 'rgba(239,71,111,0.18)' : 'transparent'};
  color: ${({ $on }) => ($on ? 'var(--alert-error)' : 'var(--text-secondary)')};
  animation: ${({ $on }) => ($on ? css`${pulseMic} 1.6s infinite` : 'none')};

  &:hover:not(:disabled) { color: var(--text-primary); border-color: rgba(0,180,216,0.5); }
  &:active:not(:disabled) { transform: scale(0.95); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const Hint = styled.div`
  text-align: center;
  font-family: ${mono};
  font-size: 0.58rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--text-disabled);
  margin-top: 0.5rem;
`;

/* quick asks */
const QuickWrap = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 0.55rem;
  margin-bottom: 1.1rem;
`;
const QuickBtn = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.65rem 0.8rem;
  background: var(--surface-deep);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius-sm);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 0.84rem;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.16s ease, background 0.16s ease, transform 0.16s ease;
  svg { color: ${accent}; flex-shrink: 0; margin-top: 2px; }
  &:hover { border-color: rgba(0,180,216,0.35); background: rgba(0,180,216,0.06); color: var(--text-primary); transform: translateY(-1px); }
`;

/* ---------- gestionar: acciones ---------- */
const GroupTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1.4rem 0 0.7rem;
  font-family: ${mono};
  font-size: 0.64rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--text-disabled);
  font-weight: 500;
  &::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: ${({ $color }) => $color || accent}; }
  &::after { content: ''; flex: 1; height: 1px; background: var(--border-soft); }
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 0.7rem;
`;

const ActionCard = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  padding: 0.85rem 0.9rem;
  background: var(--surface);
  border: 1px solid var(--border-soft);
  border-left: 2px solid ${({ $color }) => $color || accent};
  border-radius: var(--radius);
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  transition: transform 0.15s ease, background 0.15s ease;
  &:hover { transform: translateY(-2px); background: rgba(255,255,255,0.03); }

  .ico {
    display: inline-flex; align-items: center; justify-content: center;
    width: 34px; height: 34px; flex-shrink: 0; border-radius: var(--radius-sm);
    font-size: 0.9rem; color: ${({ $color }) => $color || accent};
    background: ${({ $color }) => ($color ? `${$color}1a` : 'rgba(0,180,216,0.1)')};
    border: 1px solid ${({ $color }) => ($color ? `${$color}44` : 'rgba(0,180,216,0.28)')};
  }
  .label { display: block; font-size: 0.9rem; font-weight: 500; color: var(--text-primary); }
  .desc { display: block; font-size: 0.76rem; color: var(--text-disabled); margin-top: 2px; line-height: 1.35; }
`;

/* composer de una acción */
const ActionPanel = styled.div`
  background: var(--surface);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius-lg);
  padding: 1.1rem 1.2rem 1.2rem;
`;
const BackLink = styled.button`
  display: inline-flex; align-items: center; gap: 0.4rem;
  background: none; border: none; cursor: pointer;
  color: var(--text-disabled); font-family: inherit; font-size: 0.8rem;
  margin-bottom: 0.8rem;
  &:hover { color: var(--text-secondary); }
`;
const Field = styled.label`
  display: block;
  margin-bottom: 0.8rem;
  .lbl { display: block; font-size: 0.82rem; color: var(--text-secondary); margin-bottom: 0.35rem; }
  input, textarea, select {
    width: 100%;
    background: var(--surface-deep);
    border: 1px solid var(--border-soft);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    font-family: inherit;
    font-size: 0.9rem;
    padding: 0.6rem 0.7rem;
    outline: none;
    &:focus { border-color: rgba(0,180,216,0.45); box-shadow: 0 0 0 2px rgba(0,180,216,0.12); }
  }
  textarea { resize: vertical; min-height: 74px; line-height: 1.45; }
`;
const GenerateBtn = styled.button`
  display: inline-flex; align-items: center; gap: 0.5rem;
  padding: 0.6rem 1.1rem;
  border: 1px solid rgba(0,180,216,0.45);
  background: linear-gradient(135deg, rgba(0,180,216,0.3), rgba(0,180,216,0.15));
  color: var(--text-primary);
  border-radius: var(--radius-sm);
  font-family: inherit; font-size: 0.88rem; font-weight: 500; cursor: pointer;
  transition: background 0.16s ease;
  &:hover:not(:disabled) { background: linear-gradient(135deg, rgba(0,180,216,0.45), rgba(0,180,216,0.22)); }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

/* resultado */
const Result = styled.div`
  margin-top: 1.1rem;
  border-top: 1px solid var(--border-soft);
  padding-top: 1rem;
`;
const ResultBar = styled.div`
  display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;
  margin-bottom: 0.7rem;
`;
const SmallBtn = styled.button`
  display: inline-flex; align-items: center; gap: 0.4rem;
  padding: 0.4rem 0.7rem;
  background: var(--surface-deep);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius-sm);
  color: var(--text-secondary);
  font-family: inherit; font-size: 0.78rem; cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
  &:hover:not(:disabled) { color: var(--text-primary); border-color: rgba(0,180,216,0.4); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  svg { font-size: 0.75rem; }
`;
const RelatedLink = styled(Link)`
  display: inline-flex; align-items: center; gap: 0.4rem;
  margin-left: auto;
  font-size: 0.78rem; color: ${accent}; text-decoration: none;
  &:hover { text-decoration: underline; }
`;
const ErrorNote = styled.div`
  background: rgba(239,71,111,0.08);
  border: 1px solid rgba(239,71,111,0.35);
  color: #ffc9d4;
  border-radius: var(--radius-sm);
  padding: 0.7rem 0.9rem;
  font-size: 0.86rem;
`;

/* ============================================================
   Dictado por voz (Web Speech API). Silencioso si no hay soporte.
   ============================================================ */
function useDictation(onText) {
  const recRef = useRef(null);
  const [listening, setListening] = useState(false);
  const Supported = typeof window !== 'undefined'
    && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const stop = () => { try { recRef.current?.stop(); } catch { /* noop */ } setListening(false); };

  const start = () => {
    if (!Supported) return;
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new Rec();
    rec.lang = 'es-AR';
    rec.interimResults = false;
    rec.continuous = false;
    rec.onresult = (e) => {
      const text = Array.from(e.results).map((r) => r[0].transcript).join(' ').trim();
      if (text) onText(text);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    try { rec.start(); setListening(true); } catch { setListening(false); }
  };

  useEffect(() => () => { try { recRef.current?.stop(); } catch { /* noop */ } }, []);

  return { supported: !!Supported, listening, start, stop };
}

const MdView = ({ text }) => (
  <Md>
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
  </Md>
);

const nowTs = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/* ============================================================
   Modo Consultar (chat)
   ============================================================ */
function ConsultMode() {
  const [messages, setMessages] = useState([]);
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const taRef = useRef(null);

  const dictation = useDictation((t) => setValue((v) => (v ? `${v} ${t}` : t)));

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  const send = async (text) => {
    const trimmed = (text ?? value).trim();
    if (!trimmed || loading) return;
    const userMsg = { id: `u-${Date.now()}`, text: trimmed, user: true, ts: nowTs() };
    const next = [...messages, userMsg];
    setMessages(next);
    setValue('');
    setLoading(true);
    try {
      const history = next.filter((m) => !m.error).map((m) => ({
        role: m.user ? 'user' : 'assistant', content: m.text,
      }));
      const data = await sendChat(history);
      setMessages((prev) => [...prev, { id: `a-${Date.now()}`, text: data.answer, user: false, ts: nowTs() }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error || err.message || 'No se pudo responder.';
      setMessages((prev) => [...prev, { id: `e-${Date.now()}`, text: detail, user: false, error: true, ts: nowTs() }]);
    } finally {
      setLoading(false);
      requestAnimationFrame(() => taRef.current?.focus());
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      {messages.length === 0 && (
        <QuickWrap>
          {QUICK_ASKS.map((q) => (
            <QuickBtn key={q} type="button" onClick={() => send(q)}>
              <FaBolt size={12} /> {q}
            </QuickBtn>
          ))}
        </QuickWrap>
      )}

      <Thread>
        {messages.map((m) => (
          <MsgRow key={m.id} $user={m.user}>
            <Meta>{m.user ? <FaUser /> : <FaRobot />} {m.user ? 'Vos' : 'Copiloto'} · {m.ts}</Meta>
            <Bubble $user={m.user} $error={m.error}>
              {m.user ? m.text : <MdView text={m.text} />}
            </Bubble>
          </MsgRow>
        ))}
        {loading && (
          <MsgRow>
            <Meta><FaRobot /> Copiloto · pensando</Meta>
            <TypingBubble>Procesando <Dots><span /><span /><span /></Dots></TypingBubble>
          </MsgRow>
        )}
        <div ref={endRef} />
      </Thread>

      <Composer onSubmit={(e) => { e.preventDefault(); send(); }}>
        <textarea
          ref={taRef}
          rows={1}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKey}
          placeholder="Preguntale lo que quieras sobre tu operación…"
          disabled={loading}
        />
        {dictation.supported && (
          <RoundBtn
            type="button"
            $on={dictation.listening}
            onClick={dictation.listening ? dictation.stop : dictation.start}
            aria-label={dictation.listening ? 'Detener dictado' : 'Dictar por voz'}
            title={dictation.listening ? 'Detener' : 'Dictar por voz'}
          >
            {dictation.listening ? <FaStopCircle /> : <FaMicrophone />}
          </RoundBtn>
        )}
        <RoundBtn type="submit" $variant="send" disabled={loading || !value.trim()} aria-label="Enviar">
          <FaPaperPlane size={14} />
        </RoundBtn>
      </Composer>
      <Hint>Enter para enviar · Shift+Enter nueva línea{dictation.supported ? ' · 🎙 dictado por voz' : ''}</Hint>
    </>
  );
}

/* ============================================================
   Modo Gestionar (acciones)
   ============================================================ */
function ManageMode() {
  const [active, setActive] = useState(null); // action object
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const openAction = (a) => {
    setActive(a);
    const init = {};
    (a.inputs || []).forEach((f) => { init[f.name] = f.type === 'select' ? (f.options?.[0] || '') : ''; });
    setValues(init);
    setResult(null);
    setError(null);
  };

  const close = () => { setActive(null); setResult(null); setError(null); };

  const canGenerate = useMemo(() => {
    if (!active) return false;
    return (active.inputs || []).every((f) => !f.required || (values[f.name] || '').trim());
  }, [active, values]);

  const run = async () => {
    if (!active || loading) return;
    setLoading(true);
    setError(null);
    try {
      const prompt = active.buildPrompt(values);
      const data = await sendChat([{ role: 'user', content: prompt }]);
      setResult(data.answer);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.response?.data?.error || err.message || 'No se pudo generar el borrador.');
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!result) return;
    try { await navigator.clipboard.writeText(result); setCopied(true); setTimeout(() => setCopied(false), 1600); }
    catch { /* noop */ }
  };

  if (active) {
    const ActIcon = active.icon;
    const group = ACTION_GROUPS.find((g) => g.id === active.group);
    return (
      <ActionPanel>
        <BackLink type="button" onClick={close}><FaArrowLeft /> Volver a las acciones</BackLink>
        <Head style={{ marginBottom: '1rem' }}>
          <span className="badge" style={{ color: group?.color, background: `${group?.color}1a`, borderColor: `${group?.color}44` }}>
            <ActIcon />
          </span>
          <div>
            <h1 style={{ fontSize: '1.2rem' }}>{active.label}</h1>
            <p>{active.desc}</p>
          </div>
        </Head>

        {(active.inputs || []).map((f) => (
          <Field key={f.name}>
            <span className="lbl">{f.label}{f.required ? ' *' : ''}</span>
            {f.type === 'select' ? (
              <select value={values[f.name] || ''} onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}>
                {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : f.type === 'textarea' ? (
              <textarea
                value={values[f.name] || ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
                placeholder={f.placeholder}
              />
            ) : (
              <input
                type="text"
                value={values[f.name] || ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
                placeholder={f.placeholder}
              />
            )}
          </Field>
        ))}

        <GenerateBtn type="button" onClick={run} disabled={!canGenerate || loading}>
          <FaMagic /> {loading ? 'Generando…' : result ? 'Generar de nuevo' : 'Generar borrador'}
        </GenerateBtn>

        {loading && (
          <div style={{ marginTop: '1rem' }}>
            <TypingBubble>La IA está redactando <Dots><span /><span /><span /></Dots></TypingBubble>
          </div>
        )}

        {error && <Result><ErrorNote>{error}</ErrorNote></Result>}

        {result && !loading && (
          <Result>
            <ResultBar>
              <SmallBtn type="button" onClick={copy}>
                {copied ? <FaCheck /> : <FaRegCopy />} {copied ? 'Copiado' : 'Copiar'}
              </SmallBtn>
              <SmallBtn type="button" onClick={run}><FaRedo /> Regenerar</SmallBtn>
              {active.related && (
                <RelatedLink to={active.related.to}>
                  Ir a {active.related.label} <FaArrowRight />
                </RelatedLink>
              )}
            </ResultBar>
            <Bubble as="div">
              <MdView text={result} />
            </Bubble>
          </Result>
        )}
      </ActionPanel>
    );
  }

  return (
    <>
      {ACTION_GROUPS.map((group) => {
        const items = ACTIONS.filter((a) => a.group === group.id);
        if (!items.length) return null;
        return (
          <React.Fragment key={group.id}>
            <GroupTitle $color={group.color}>{group.label}</GroupTitle>
            <ActionGrid>
              {items.map((a) => {
                const Icon = a.icon;
                return (
                  <ActionCard key={a.id} type="button" $color={group.color} onClick={() => openAction(a)}>
                    <span className="ico"><Icon /></span>
                    <span>
                      <span className="label">{a.label}</span>
                      <span className="desc">{a.desc}</span>
                    </span>
                  </ActionCard>
                );
              })}
            </ActionGrid>
          </React.Fragment>
        );
      })}
    </>
  );
}

/* ============================================================
   Página
   ============================================================ */
const Copiloto = () => {
  const { user } = useAuth();
  const [mode, setMode] = useState('consultar');
  const firstName = (user?.name || '').split(/[\s@]/)[0] || 'Federico';

  return (
    <Page>
      <Head>
        <span className="badge"><FaRobot /></span>
        <div>
          <h1>Copiloto de {firstName}</h1>
          <p>Tu asistente con acceso a toda tu operación. Consultá lo que quieras o dejá que te resuelva un borrador listo para usar.</p>
        </div>
      </Head>

      <Tabs role="tablist">
        <TabBtn $active={mode === 'consultar'} onClick={() => setMode('consultar')} role="tab" aria-selected={mode === 'consultar'}>
          <FaCommentDots /> Consultar
        </TabBtn>
        <TabBtn $active={mode === 'gestionar'} onClick={() => setMode('gestionar')} role="tab" aria-selected={mode === 'gestionar'}>
          <FaMagic /> Gestionar
        </TabBtn>
      </Tabs>

      {mode === 'consultar' ? <ConsultMode /> : <ManageMode />}
    </Page>
  );
};

export default Copiloto;
